/**
 * CustomerLayout — authenticated portal shell.
 *
 * Ported (and trimmed) from `screens/src/layouts/CustomerLayout.jsx`. Renders
 * a sticky topbar with logo + nav + user dropdown plus an `<Outlet />` for
 * the active page.
 *
 * Phase 1c wires the full self-serve toolbox: dashboard, shipments, tracking,
 * profile, notifications PLUS book / drafts / complaints / address /
 * reschedule / pickup / outlets / topup / preferences / settings /
 * registration. The mobile menu surfaces the secondary group beneath the
 * primary nav with a "Sign out" affordance at the bottom.
 */
import { useMemo, useState, type JSX } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  Building2,
  Calendar,
  ChevronDown,
  CreditCard,
  FileEdit,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  Package,
  Search,
  Settings,
  ShieldCheck,
  Sliders,
  User,
  Wallet,
  X,
} from 'lucide-react';

import { useCustomerAuth } from '../lib/auth/useCustomerAuth';
import { useCustomerProfile } from '../features/portal/useCustomerProfile';

interface NavItem {
  readonly key: string;
  readonly label: string;
  readonly path: string;
  readonly icon: typeof Package;
}

const PRIMARY_NAV: readonly NavItem[] = [
  { key: 'dashboard',     label: 'Dashboard',     path: '/portal/dashboard',     icon: LayoutDashboard },
  { key: 'shipments',     label: 'My Shipments',  path: '/portal/shipments',     icon: Package },
  { key: 'tracking',      label: 'Track',         path: '/portal/tracking',      icon: Search },
  { key: 'profile',       label: 'Profile',       path: '/portal/profile',       icon: User },
  { key: 'notifications', label: 'Notifications', path: '/portal/notifications', icon: Bell },
];

/** Phase 1c — secondary nav group. Surfaced beneath the primary group on
 *  the mobile menu and inside the dropdown for desktop. Each item is now a
 *  live route (replacing the Phase-1b "coming soon" placeholders). */
const SECONDARY_NAV: readonly NavItem[] = [
  { key: 'parcel-booking',  label: 'Book a Parcel',     path: '/portal/parcel-booking',  icon: Package },
  { key: 'drafts',          label: 'Drafts',            path: '/portal/drafts',          icon: FileEdit },
  { key: 'topup',           label: 'Top up Wallet',     path: '/portal/topup',           icon: Wallet },
  { key: 'complaints',      label: 'Submit Complaint',  path: '/portal/complaints/new',  icon: MessageSquare },
  { key: 'address-change',  label: 'Address Change',    path: '/portal/address-change',  icon: CreditCard },
  { key: 'reschedule',      label: 'Reschedule',        path: '/portal/reschedule',      icon: Calendar },
  { key: 'pickup',          label: 'Pickup Location',   path: '/portal/pickup',          icon: Building2 },
  { key: 'outlets',         label: 'Find an Outlet',    path: '/portal/outlets',         icon: Building2 },
  { key: 'preferences',     label: 'Delivery Preferences', path: '/portal/preferences',  icon: Sliders },
  { key: 'settings',        label: 'Settings',          path: '/portal/settings',        icon: Settings },
  { key: 'register',        label: 'Registration',      path: '/portal/register',        icon: ShieldCheck },
];

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return 'C';
  }
  if (parts.length === 1) {
    return (parts[0]?.[0] ?? 'C').toUpperCase();
  }
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
}

export function CustomerLayout(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { claims, logout } = useCustomerAuth();
  const profile = useCustomerProfile();
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const displayName = useMemo(() => {
    if (profile.data?.fullName) {
      return profile.data.fullName;
    }
    if (profile.data?.displayName) {
      return profile.data.displayName;
    }
    return claims?.displayName ?? 'Customer';
  }, [profile.data, claims]);

  const initials = initialsFor(displayName);
  const shortName = displayName.split(/\s+/)[0] ?? 'Customer';

  function handleSignOut(): void {
    setDropdownOpen(false);
    logout();
    navigate('/', { replace: true });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-30 bg-white border-b border-border shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/portal/dashboard" className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-nipost-green flex items-center justify-center">
                  <span className="text-white font-bold text-sm">N</span>
                </div>
                <span className="font-bold text-gray-900">UPMP</span>
              </Link>

              <nav className="hidden md:flex items-center gap-1">
                {PRIMARY_NAV.map((item) => {
                  const isActive =
                    location.pathname === item.path ||
                    location.pathname.startsWith(`${item.path}/`);
                  return (
                    <NavLink
                      key={item.key}
                      to={item.path}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-nipost-green-light text-nipost-green'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      {item.label}
                    </NavLink>
                  );
                })}
              </nav>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/portal/notifications"
                className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-600 hover:bg-gray-100"
                aria-label="View notifications"
              >
                <Bell className="w-4 h-4" />
              </Link>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDropdownOpen((open) => !open)}
                  className="flex items-center gap-2 pl-3 border-l border-border hover:bg-gray-50 rounded-lg px-2 py-1.5 transition-colors"
                  aria-haspopup="menu"
                  aria-expanded={dropdownOpen}
                >
                  <div className="w-8 h-8 rounded-full bg-nipost-green flex items-center justify-center">
                    <span className="text-white text-xs font-medium">{initials}</span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-gray-700">
                    {shortName}
                  </span>
                  <ChevronDown className="w-4 h-4 text-muted" />
                </button>

                {dropdownOpen ? (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setDropdownOpen(false)}
                      aria-hidden="true"
                    />
                    <div
                      role="menu"
                      className="absolute right-0 mt-1 w-60 bg-white rounded-xl border border-border shadow-lg z-20 py-1 max-h-[80vh] overflow-y-auto"
                    >
                      <Link
                        to="/portal/profile"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        role="menuitem"
                      >
                        <User className="w-4 h-4" />
                        Profile
                      </Link>
                      <Link
                        to="/portal/notifications"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        role="menuitem"
                      >
                        <Bell className="w-4 h-4" />
                        Notifications
                      </Link>

                      <div className="my-1 border-t border-border" />
                      <p className="px-4 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted">
                        Self-service
                      </p>
                      {SECONDARY_NAV.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.key}
                            to={item.path}
                            onClick={() => setDropdownOpen(false)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            role="menuitem"
                          >
                            <Icon className="w-4 h-4" />
                            {item.label}
                          </Link>
                        );
                      })}
                      <div className="my-1 border-t border-border" />
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-danger hover:bg-gray-50 w-full text-left"
                        role="menuitem"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  </>
                ) : null}
              </div>

              <button
                type="button"
                className="md:hidden p-2 rounded-lg hover:bg-gray-100"
                onClick={() => setMobileMenuOpen((open) => !open)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X className="w-5 h-5 text-gray-600" /> : <Menu className="w-5 h-5 text-gray-600" />}
              </button>
            </div>
          </div>

          {mobileMenuOpen ? (
            <nav className="md:hidden pb-3 border-t border-border pt-2 space-y-1">
              {PRIMARY_NAV.map((item) => {
                const isActive =
                  location.pathname === item.path ||
                  location.pathname.startsWith(`${item.path}/`);
                return (
                  <Link
                    key={item.key}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-3 py-2 rounded-lg text-sm font-medium ${
                      isActive
                        ? 'bg-nipost-green-light text-nipost-green'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <div className="pt-2 mt-2 border-t border-border">
                <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted">
                  Self-service
                </p>
                {SECONDARY_NAV.map((item) => {
                  const isActive =
                    location.pathname === item.path ||
                    location.pathname.startsWith(`${item.path}/`);
                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block px-3 py-2 rounded-lg text-sm ${
                        isActive
                          ? 'bg-nipost-green-light text-nipost-green font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
              <div className="pt-2 mt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleSignOut();
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-danger hover:bg-gray-100 w-full text-left"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            </nav>
          ) : null}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
